import React from 'react';
import config from 'cfg';
import PropTypes from 'prop-types';
import RCore from 'RCore';
import styled from 'styled-components';
import get from 'lodash/get';
import BaseWidget from 'BaseWidget';
import { loadData as templateLoadData, SWTProvider } from '../core/SWTProvider';
import { styledUI, getDisplayPropType } from './StyledUI';
import Pitch3d from '../ui/svg/pitch';

const scaleFontSize = (value, defaultSize = 18) => {
    if (value.length < 13) return '';
    const size = defaultSize - (value.length - 12 + 1);

    return `font-size: ${size >= 12 ? size : 12}px;`;
};

const Content = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: 1;
    padding: 9px 0;
    overflow: hidden;
    box-sizing: border-box;
    margin: 0 -${({ theme }) => theme.contentPadding}px;
    margin-bottom: -${({ theme }) => 100 - 100 * (1 - (theme.contentPadding * 3 * (100 / 800)) / 100)}%;
    transform-origin: top;
    transform: scale(calc(1 - (((${({ theme }) => theme.contentPadding}*2) * (100 / 800)) / 100)));
`;

const Row = styled.div`
    display: flex;
    align-items: center;
    justify-content: ${({ type }) => (type === 'grid' ? 'center' : 'space-around')};
    padding: 0 20px;
    margin-bottom: 13px;

    > div {
        margin: 0 9px;
    }
`;

const Unknown = styled.div`
    position: relative;
    width: 140px;
    height: 150px;
    flex-shrink: 0;
    box-shadow: 0 0 9px 0 rgba(0, 0, 0, 0.4);
    background-color: rgba(0, 0, 0, 0.6);
    border-style: solid;
    border-width: 2px;
    border-image-source: radial-gradient(
        circle at 32% 17%,
        #fff,
        rgba(0, 49, 85, 0.5) 50%,
        rgba(0, 0, 0, 0.5) 85%,
        #fff
    );
    border-image-slice: 1;
    ${({ type }) => type === 'grid' && 'width: 160px; height: 210px;'};

    ${({ large }) =>
        large &&
        `
        width: 192px;
        height: 251px;
        padding: 2px;
    `};

    span,
    span.__c__c5 {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: ${({ large }) => (large ? '160px' : '120px')};
        line-height: 1;
        color: rgba(255, 255, 255, 0.8);
        background: transparent !important;
    }
`;

const Div = styled.div`
    display: ${({ type }) => type !== 'grid' && 'none'};
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1 !important;
    box-shadow: 0 0 9px 0 rgba(0, 0, 0, 0.5);
    background-color: rgba(0, 0, 0, 0.6);
    border-style: solid;
    border-width: 2px;
    border-image-source: radial-gradient(
        circle at 32% 17%,
        #fff,
        rgba(0, 49, 85, 0.5) 50%,
        rgba(0, 0, 0, 0.5) 85%,
        #fff
    );
    border-image-slice: 1;
`;

const Participant = styled.div`
    position: relative;
    width: 140px;
    height: 150px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    flex-shrink: 0;
    font-size: 20px;
    font-weight: 700;
    text-align: center;
    color: #fff;

    ${({ type }) =>
        type === 'grid' &&
        `
        width: 160px;
        height: 209px;
        padding: 2px;
    `};

    ${({ large }) =>
        large &&
        `
        width: 192px;
        height: 251px;
        padding: 2px;
    `};

    > * {
        z-index: 2;
    }
`;

const Avatar = styled.div`
    position: relative;
    width: 140px;
    z-index: 3;
    display: flex;
    justify-content: center;
    pointer-events: none;
    ${({ type }) => type === 'teams' && 'align-items: center;'};
    ${({ lineupType }) => (lineupType === 'grid' ? 'width: 156px; height: 150px;' : 'height: 100px;')};
    ${({ large }) => large && 'width: 188px; height: 181px'};

    span span img {
        width: 100%;
        max-height: 100%;

        &.wdp-sm-plr-logo-rounded {
            max-height: none;
        }

        &.wdp-sm-plr-logo_jersey {
            max-height: 90%;
            ${({ lineupType }) => lineupType === 'grid' && 'margin-top: 15px;'};
        }

        &.wdp-sm-plr-logo-spdk {
            ${({ lineupType }) => lineupType === 'grid' && 'width: 122%; margin-left: -11%;'};
        }
    }

    .t-shirt-container {
        width: 140px;
        display: inherit;
        justify-content: center;

        .t-shirt-front {
            object-fit: contain;
            width: 100%;
        }
    }
`;

const TeamLogoBadge = styled.div`
    position: absolute;
    bottom: -4px;
    left: 0;
    z-index: 2;
    ${({ type }) => type === 'grid' && 'left: -12px;'};
`;

const TextTop = styled.h4`
    height: 25px;
    margin: 0;
    padding: 0 2px;
    font-size: 18px;
    line-height: 24px;
    font-weight: ${({ theme }) => theme.fonts.main.bold || '700'};
    background: #005caa !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    ${({ value }) => scaleFontSize(value, 18)};
    ${({ type, value }) => type === 'grid' && `height: 30px; ${scaleFontSize(value, 19)} line-height: 30px;`};
    ${({ large, value }) => large && `height: 36px; ${scaleFontSize(value, 22)} line-height: 36px;`};

    span,
    span.__c__c1,
    span.__c__c2,
    span.__c__c3,
    span.__c__c4 {
        background: transparent !important;
    }
`;

const TextInputBottom = styled.h5`
    height: 25px;
    margin: 0;
    padding: 0 6px;
    font-size: 19px;
    line-height: 24px;
    font-weight: ${({ theme }) => theme.fonts.main.bold || '700'};
    border: none;
    background: rgba(0, 0, 0, 0.5) !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: center;
    ${({ value }) => scaleFontSize(value, 19)};
    ${({ type, theme }) => type === 'grid' && `font-weight: ${theme.fonts.main.normal || '400'};`};
    ${({ large, value }) => large && `height: 30px; ${scaleFontSize(value, 22)} line-height: 30px;`};

    span,
    span.__c__c1,
    span.__c__c2,
    span.__c__c3,
    span.__c__c4 {
        background: transparent !important;
    }
`;

const Pitch = styled.div`
    .sm-lineup__pitch {
        text-align: center;
        width: 190px;
        height: auto;
        z-index: 0;
        bottom: 0;
        ${props => props.pitchImageStyles};
    }
    svg {
        position: absolute;
        top: 89px;
        left: 0;
        z-index: -1;
        width: 870px;
        margin-left: -35px;
    }
`;

class TeamCustomSMWidget extends BaseWidget {
    constructor(props) {
        super(props);
    }

    static contextTypes = {
        info: PropTypes.object,
        screenSizeObserver: PropTypes.object,
    };

    static defaultProps = {
        idLineUp: null,
        type: '',
        players: [],
    };

    loadData() {
        let { idLineUp, players: playersProps } = this.props;

        idLineUp = parseInt(idLineUp, 10);

        const displayPlayersProps = playersProps.length;

        if (!displayPlayersProps && !idLineUp && isNaN(idLineUp)) return RCore.standardError(404, `Missing Id LineUp`);

        return RCore.promiseProps({
            template: Reflect.apply(templateLoadData, this, []),
            lineup: displayPlayersProps
                ? playersProps
                : this.fetch(`${config.apis.ss2}/admin/keyval?appName=maker-tool&key=${idLineUp}`).then(
                      res => res.value,
                  ),
        })
            .then(res => {
                return RCore.promiseProps({ ...res, type: res.lineup.type, players: res.lineup.players });
            })
            .catch(e => {
                return RCore.standardError(404, e.message);
            });
    }

    getImage = teamId => {
        // const { tournamentId } = this.props;
        //
        // if (tournamentId === 46) {
        //     return `https://s3-eu-west-1.amazonaws.com/sport.images/wdp/shirts_rich/t_shirts_front_logo_${teamId}.png`;
        // }
        //
        // return `https://s3-eu-west-1.amazonaws.com/sport.images/wdp/shirts/ic_shirt_${teamId}.png?a=2`;

        return `https://s3-eu-west-1.amazonaws.com/sport.images/wdp/shirts_rich/t_shirts_front_logo_${teamId}.png`;
    };

    getPlayerLogo = participant => {
        const { template } = this.state;
        const { playerLogoDisplay } = this.props;
        const lineupType = this.props.type || this.state.type;

        const SMPlayerLogo = styledUI.getDisplay(template, 'playerLogo', this.props);

        const imageWrapStyle = {
            display: 'block',
            overflow: 'hidden',
        };

        if (playerLogoDisplay === 'PlayerLogo3DJerseyFront') {
            return (
                <img
                    className="t-shirt-front"
                    src={this.getImage(participant.team ? participant.team.id : 0)}
                    alt="img"
                />
            );
        }

        return (
            <SMPlayerLogo
                teamId={participant.team ? participant.team.id : 0}
                playerId={participant.id}
                seasonId="current"
                isGK={participant.position === 'keeper'}
                size="large"
                imageWrapStyle={imageWrapStyle}
                number={participant.shirtNumber}
                nameOnShirt3dJerseySize={lineupType === 'grid' ? 150 : 100}
            />
        );
    };

    getAvatar = (participant, large = false) => {
        const { template } = this.state;
        const type = participant.countryId ? 'teams' : 'players';
        const SMTeamLogo = styledUI.getDisplay(template, 'teamLogo', this.props);
        const lineupType = this.props.type || this.state.type;

        return (
            <Avatar type={type} lineupType={lineupType} large={large}>
                {type === 'teams' && (
                    <SMTeamLogo teamId={participant.id} width={large ? 160 : lineupType === 'grid' ? 130 : 95} />
                )}
                {type === 'players' && [
                    <span key="playerLogo" className="t-shirt-container">
                        {this.getPlayerLogo(participant)}
                    </span>,
                    <TeamLogoBadge key="TeamLogoBadge" type={lineupType} large={large}>
                        <SMTeamLogo teamId={participant.team ? participant.team.id : 0} width={(large && 45) || 35} />
                    </TeamLogoBadge>,
                ]}
            </Avatar>
        );
    };

    renderParticipant(participant, large) {
        const lineupType = this.props.type || this.state.type;

        if (participant.unknown) {
            return (
                <Unknown
                    key={participant.id}
                    type={lineupType}
                    large={large}
                    className={lineupType === 'grid' ? '__c__b5' : ''}
                >
                    <span className={lineupType === 'grid' ? '__c__c5' : ''}>?</span>
                </Unknown>
            );
        }

        return (
            <Participant key={participant.id} type={lineupType} large={large}>
                <Div type={lineupType} className={lineupType === 'grid' ? '__c__b6' : ''} />
                {this.getAvatar(participant, large)}
                {participant.name && (
                    <TextTop
                        type={lineupType}
                        className={lineupType === 'grid' ? '__c__b1' : '__c__b2'}
                        large={large}
                        value={participant.name}
                    >
                        <span
                            style={{ background: 'transparent !important' }}
                            className={lineupType === 'grid' ? '__c__c1' : '__c__c2'}
                        >
                            {participant.name}
                        </span>
                    </TextTop>
                )}
                {participant.customText && (
                    <TextInputBottom
                        type={lineupType}
                        className={lineupType === 'grid' ? '__c__b3' : '__c__b4'}
                        large={large}
                        value={participant.customText}
                    >
                        <span
                            style={{ background: 'transparent !important' }}
                            className={lineupType === 'grid' ? '__c__c3' : '__c__c4'}
                        >
                            {participant.customText}
                        </span>
                    </TextInputBottom>
                )}
            </Participant>
        );
    }

    render() {
        const { template, __status, type: lineupTypeState = 'grid', players: playersState = [] } = this.state;
        const { type: lineupTypeProps = 'grid', players: playersProps = [], title, subTitle } = this.props;

        if (__status || (!(playersProps && playersProps.length) && !(playersState && playersState.length))) {
            return this.smRenderEmpty();
        }

        const lineupType = lineupTypeProps || lineupTypeState;
        const players = playersProps.length ? playersProps : playersState;

        const lineupTemplate = Object.entries(
            Array.from(players).reduce((acc, curr) => {
                if (curr.positionCode < 1) return { ...acc };
                const position = curr.positionCode.charAt(0);

                if (acc[position]) return { ...acc, [position]: [...acc[position], curr] };

                return { ...acc, [position]: [curr] };
            }, {}),
        );

        const templateLength = lineupTemplate.length;
        const pitchTheme = get(this, 'state.template.theme.pitch', {});
        const large =
            lineupType === 'grid' &&
            players.filter(player => player.positionCode > 9 && player.positionCode < 20).length === players.length;

        return (
            <SWTProvider
                {...this.props}
                templateData={template}
                title={title}
                subTitle={subTitle}
                contentProps={lineupType === 'lineup' ? { padding: 0, margin: 0 } : {}}
                // contentProps={{
                //     padding: 0,
                //     margin: 0,
                // }}
            >
                <div empty />
                <div>
                    <Content>
                        {lineupTemplate.map(([rowKey, rowVal]) => (
                            <Row key={Math.random() + Date.now() + rowKey} offset={templateLength}>
                                {rowVal.map(participant => this.renderParticipant(participant, large))}
                            </Row>
                        ))}
                        {lineupType === 'lineup' && (
                            <Pitch>
                                <Pitch3d {...pitchTheme} />
                            </Pitch>
                        )}
                    </Content>
                </div>
            </SWTProvider>
        );
    }
}

const spec = (TeamCustomSMWidget.spec = {
    id: 'TeamCustomSMWidget',
    social: true,
    propTypes: [
        {
            key: 'idLineUp',
            label: 'LineUp Id',
            type: 'textfield_picker',
            defaultValue: TeamCustomSMWidget.defaultProps.idLineUp,
            loader: true,
            required: true,
        },
        getDisplayPropType('playerLogo', { ui: true }),
        ...SWTProvider.spec.propTypes,
    ],
    demo: [
        {
            title: 'Test widget LineUp',
            props: {
                title: 'title',
                subTitle: 'sub title',
                idLineUp: 28659,
            },
        },
        {
            title: 'Test widget Grid',
            props: {
                title: 'title',
                subTitle: 'sub title',
                idLineUp: 33809,
            },
        },
        {
            title: 'Test widget Grid Large',
            props: {
                title: 'title',
                subTitle: 'sub title',
                idLineUp: 10976,
            },
        },
    ],
    categories: ['events'],
    //v2Categories: [BaseWidget.CATEGORIES.V2.EDITORIAL],
});

const i18 = (TeamCustomSMWidget.i18 = {
    da: {},
    en: {},
});

export default TeamCustomSMWidget;
export { TeamCustomSMWidget, spec, i18 };



/** WEBPACK FOOTER **
 ** ../src/widgets/components/sm/TeamCustomSMWidget.js
 **/